import { z } from "zod";

// =============================================================
// Module local « x-appels » (PROTECTO) : le répondeur IA — réglages de
// l'assistant qui décroche, appels en cours et historique. La téléphonie et
// l'agent conversationnel vivent ailleurs (briques internes) : ce module
// stocke ce qu'ils lui poussent et l'affiche aux techniciens.
// =============================================================

export type CallDirection = "entrant" | "sortant";
export type CallStatus = "en_cours" | "termine" | "manque" | "transfere";
export type CallOutcome =
  "rdv_pris" | "message" | "renseignement" | "transfert" | "rappel" | "aucun";
export type Speaker = "ia" | "client";

export interface TranscriptLine {
  /** Secondes depuis le début de l'appel. */
  at: number;
  speaker: Speaker;
  text: string;
}

/**
 * D'où vient l'appel : un vrai appel téléphonique (identifiant Twilio `CA…`)
 * ou une simulation (relais WebSocket textuel, scénario en mémoire). Dérivé de
 * `providerId`, jamais saisi : un appel sans identifiant fournisseur est traité
 * comme une simulation.
 */
export type CallOrigin = "telephone" | "simulation";

/**
 * Ce que la brique vocale savait d'elle-même au moment de l'appel : moteur,
 * révisions, modèles, nombre de tours servis sans modèle. Poussé par la
 * téléphonie dans l'instantané ; `null` pour un appel enregistré avant cette
 * version. Aucun prompt, secret ni donnée personnelle.
 */
export interface CallProvenance {
  /** `blueprint`, `legacy`, ou autre valeur fournie par la brique vocale. */
  engine: string | null;
  /** SHA de l'application vocale livrée (VOICE_APP_REVISION). */
  appRevision: string | null;
  /** SHA du moteur commun importé (manifeste Blueprint). */
  engineRevision: string | null;
  profile: string | null;
  fastModel: string | null;
  smartModel: string | null;
  /** Identifiant de voix et réglages de synthèse résolus au début de l'appel. */
  voice: string | null;
  /** Tours servis par un parcours préparé, sans modèle. */
  preparedTurns: number | null;
  /** Requêtes réellement envoyées au modèle. */
  modelCalls: number | null;
  /** Durée serveur cumulée des requêtes modèle, en millisecondes. */
  modelMs: number | null;
}

export interface Call {
  id: string;
  /** Identifiant chez le fournisseur de téléphonie (clé d'ingestion). */
  providerId: string | null;
  direction: CallDirection;
  status: CallStatus;
  outcome: CallOutcome;
  fromNumber: string;
  toNumber: string | null;
  /** Nom donné par l'appelant ou reconnu d'après la fiche client. */
  callerName: string | null;
  clientId: string | null;
  appointmentId: string | null;
  startedAt: number | string;
  endedAt: number | string | null;
  durationSec: number;
  /** Ce que l'appelant voulait, en trois mots (« Prix céramique »). */
  intent: string | null;
  summary: string | null;
  transcript: TranscriptLine[];
  vehicle: string | null;
  service: string | null;
  /** Créneau demandé, tel que dit (« mardi prochain vers 9 h »). */
  requestedAt: string | null;
  recordingUrl: string | null;
  notes: string | null;
  /** Vu et pris en charge par l'équipe (message rappelé, RDV vérifié…). */
  handled: boolean;
  /** Téléphone ou simulation, dérivé de `providerId`. */
  origin: CallOrigin;
  provenance: CallProvenance | null;
  /** Demande de rappel ou d'annulation telle que la brique vocale l'a transmise. */
  callback: CallCallback | null;
  createdAt: number | string;
  updatedAt: number | string;
}

export const TWILIO_CALL_SID = /^CA[0-9a-f]{32}$/i;

/** Un identifiant Twilio désigne un appel téléphonique ; tout le reste est une simulation. */
export function callOrigin(providerId: string | null | undefined): CallOrigin {
  return providerId && TWILIO_CALL_SID.test(providerId) ? "telephone" : "simulation";
}

// ---- Supervision (lecture seule, jeton Bearer) ----

export type DeployEnvironment = "prod" | "preprod";

/** Ce que l'instance dit d'elle-même à l'outil de supervision. */
export interface MonitoringSource {
  client: string;
  /** `preprod` si DEPLOY_ENV=preprod, sinon `prod` (convention de déploiement). */
  environment: DeployEnvironment;
  /** Valeur brute de DEPLOY_ENV, pour détecter une instance mal configurée. */
  deployEnv: string | null;
  phoneNumber: string | null;
  generatedAt: string;
}

export interface MonitoringStats {
  today: number;
  week: number;
  inProgress: number;
  total: number;
  lastCallAt: string | null;
}

/** Fiche sans la transcription : la liste reste légère, le détail la complète. */
export type MonitoringCallSummary = Omit<Call, "transcript"> & {
  /** Répliques de la transcription (toutes, puis celles de l'appelant). */
  turns: number;
  clientTurns: number;
};

export const MONITORING_CONTRACT = 1;

export interface MonitoringPingResponse {
  contract: number;
  source: MonitoringSource;
  stats: MonitoringStats;
}

export interface MonitoringCallsResponse {
  contract: number;
  source: MonitoringSource;
  calls: MonitoringCallSummary[];
  /** Vrai si des appels plus anciens existent au-delà de `limit` (paginer avec `before`). */
  hasMore: boolean;
}

export interface MonitoringCallResponse {
  contract: number;
  source: MonitoringSource;
  call: Call;
}

/** État du jeton de supervision tel que le portail admin le voit ; jamais le jeton lui-même. */
export interface MonitoringTokenStatus {
  configured: boolean;
  /** Quatre derniers caractères, pour reconnaître le jeton collé dans l'outil de supervision. */
  hint: string | null;
  createdAt: string | null;
}

export type Weekday = "1" | "2" | "3" | "4" | "5" | "6" | "7";
export interface DayHours {
  open: string;
  close: string;
}

export const VOICE_MODES = ["current", "elevenlabs", "openai"] as const;
export type VoiceMode = (typeof VOICE_MODES)[number];
export const VOICE_MODE_LABELS: Record<VoiceMode, string> = {
  current: "Assistant actuel",
  elevenlabs: "ElevenLabs Agents",
  openai: "OpenAI vocal",
};

export interface VoiceModeCapability {
  id: VoiceMode;
  available: boolean;
  /** Disponibilité de configuration uniquement ; ne garantit pas la qualité en appel. */
  reason?: "preprod_only" | "integration_disabled" | "configuration_required";
}

export interface AppelsCapabilities {
  femaleVoiceConfigured: boolean;
  voiceModes: {
    environment: "preprod" | "prod";
    switchEnabled: boolean;
    options: VoiceModeCapability[];
  };
}

export interface AppelsSettings {
  enabled: boolean;
  /** Sélection expérimentale préprod, appliquée aux prochains appels seulement. */
  voiceMode: VoiceMode;
  assistantName: string;
  voice: "feminine" | "masculine";
  language: "fr" | "fr-en";
  tone: "chaleureux" | "professionnel" | "direct";
  greeting: string;
  afterHoursMessage: string;
  instructions: string;
  canBook: boolean;
  canQuotePrices: boolean;
  canTakeMessages: boolean;
  /**
   * Retrouver les rendez-vous déjà pris d'un appelant, identité vérifiée (numéro
   * et nom de la fiche). Permission du projet, réglée au CRM : la brique vocale
   * ne fait que la lire.
   */
  canCheckAppointments: boolean;
  /** Noter que l'appelant confirme sa présence (« réservé » → « confirmé »). Exige la précédente. */
  canConfirmPresence: boolean;
  transferNumber: string;
  /** Transfert direct des concessionnaires reconnus pendant la disponibilité du gérant. */
  priorityDealers: boolean;
  transferPolicy: "jamais" | "sur_demande" | "urgence" | "toujours";
  /** Heures d'ouverture par jour (1 = lundi … 7 = dimanche) ; null = fermé. */
  hours: Record<Weekday, DayHours | null>;
  notifyPhone: string;
  notifyEmail: string;
  /**
   * Cellulaire personnel du propriétaire, distinct de la ligne de l'entreprise :
   * reçoit un SMS pour chaque demande de rappel ou d'annulation prise par Léo.
   */
  ownerMobile: string;
  maxCallMinutes: number;
}

// ---- Demandes transmises par Léo (rappel, annulation) ----

export type CallRequestKind = "rappel" | "annulation";
export type CallRequestStatus = "a_traiter" | "traitee";
export type RequestSmsStatus = "en_attente" | "envoye" | "simule" | "echec" | "sans_destinataire";

/**
 * Demande recueillie au téléphone : nom, numéro de rappel confirmé, raison et,
 * pour une annulation, le rendez-vous visé. Enregistrée au CRM avec un statut
 * « à traiter », reliée à l'appel, au client et au rendez-vous ; le SMS au
 * cellulaire du propriétaire porte son propre état (envoyé, échec…).
 */
export interface CallRequest {
  id: string;
  callId: string | null;
  kind: CallRequestKind;
  status: CallRequestStatus;
  callerName: string | null;
  phone: string;
  reason: string | null;
  clientId: string | null;
  appointmentId: string | null;
  /** « mardi 13 octobre à 9 h, décontamination » (figé à la demande). */
  appointmentLabel: string | null;
  smsStatus: RequestSmsStatus;
  smsAttempts: number;
  smsError: string | null;
  createdAt: number | string;
  updatedAt: number | string;
  handledAt: number | string | null;
}

export const CALL_REQUEST_KIND_LABELS: Record<CallRequestKind, string> = {
  rappel: "Rappel demandé",
  annulation: "Annulation demandée",
};

export const CALL_REQUEST_STATUS_LABELS: Record<CallRequestStatus, string> = {
  a_traiter: "À traiter",
  traitee: "Traitée",
};

export const REQUEST_SMS_STATUS_LABELS: Record<RequestSmsStatus, string> = {
  en_attente: "SMS en attente",
  envoye: "SMS envoyé",
  simule: "SMS simulé",
  echec: "SMS non envoyé",
  sans_destinataire: "Aucun cellulaire réglé",
};

/** Détail d'une demande de rappel poussé par la brique vocale avec l'instantané de l'appel. */
export interface CallCallback {
  reason: string | null;
  phone: string | null;
  intent: string | null;
  preferredCallback: string | null;
  appointmentId: string | null;
}

export interface AppelsStats {
  today: number;
  inProgress: number;
  missedToday: number;
  bookedToday: number;
  weekTotal: number;
  /** Durée moyenne des appels terminés de la semaine, en secondes. */
  avgDurationSec: number;
  /** Simulation disponible (jamais en production). */
  simulationAvailable: boolean;
}

// ---- Libellés ----

export const CALL_STATUS_LABELS: Record<CallStatus, string> = {
  en_cours: "En cours",
  termine: "Terminé",
  manque: "Manqué",
  transfere: "Transféré",
};

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  rdv_pris: "RDV pris",
  message: "Message",
  renseignement: "Renseignement",
  transfert: "Transféré",
  rappel: "À rappeler",
  aucun: "Sans suite",
};

export const CALL_DIRECTION_LABELS: Record<CallDirection, string> = {
  entrant: "Entrant",
  sortant: "Sortant",
};

export const CALL_ORIGIN_LABELS: Record<CallOrigin, string> = {
  telephone: "Téléphone",
  simulation: "Simulation",
};

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  "1": "Lundi",
  "2": "Mardi",
  "3": "Mercredi",
  "4": "Jeudi",
  "5": "Vendredi",
  "6": "Samedi",
  "7": "Dimanche",
};

export const TONE_LABELS: Record<AppelsSettings["tone"], string> = {
  chaleureux: "Chaleureux",
  professionnel: "Professionnel",
  direct: "Direct",
};

export const TRANSFER_POLICY_LABELS: Record<AppelsSettings["transferPolicy"], string> = {
  jamais: "Jamais — l'assistant prend un message",
  sur_demande: "Si l'appelant le demande",
  urgence: "Seulement en cas d'urgence",
  toujours: "Toujours, pendant les heures d'ouverture",
};

/** Regroupements pour la liste (onglets). */
export const CALL_GROUPS: Array<{
  key: string;
  label: string;
  outcomes: CallOutcome[];
  statuses: CallStatus[];
}> = [
  { key: "tous", label: "Tous", outcomes: [], statuses: [] },
  { key: "rdv", label: "RDV pris", outcomes: ["rdv_pris"], statuses: [] },
  { key: "messages", label: "Messages", outcomes: ["message", "rappel"], statuses: [] },
  { key: "renseignements", label: "Renseignements", outcomes: ["renseignement"], statuses: [] },
  { key: "manques", label: "Manqués", outcomes: [], statuses: ["manque"] },
  { key: "transferes", label: "Transférés", outcomes: [], statuses: ["transfere"] },
];

// ---- Schémas ----

export const callStatusSchema = z.enum(["en_cours", "termine", "manque", "transfere"]);
export const callOutcomeSchema = z.enum([
  "rdv_pris",
  "message",
  "renseignement",
  "transfert",
  "rappel",
  "aucun",
]);
export const callDirectionSchema = z.enum(["entrant", "sortant"]);

export const transcriptLineSchema = z.object({
  at: z.number().min(0),
  speaker: z.enum(["ia", "client"]),
  text: z.string().max(4000),
});

const isoDate = z.string().datetime({ offset: true });
const optionalText = z.string().trim().max(500).nullable().optional();

const shortText = z.string().trim().max(120).nullable().optional();
const count = z.number().int().min(0).nullable().optional();

/** Provenance poussée par la brique vocale ; chaque champ est facultatif, les inconnus sont ignorés. */
export const callProvenanceSchema = z
  .object({
    engine: z.string().trim().max(40).nullable().optional(),
    appRevision: z.string().trim().max(64).nullable().optional(),
    engineRevision: z.string().trim().max(64).nullable().optional(),
    profile: shortText,
    fastModel: shortText,
    smartModel: shortText,
    voice: shortText,
    preparedTurns: count,
    modelCalls: count,
    modelMs: count,
  })
  .transform((p): CallProvenance => ({
    engine: p.engine ?? null,
    appRevision: p.appRevision ?? null,
    engineRevision: p.engineRevision ?? null,
    profile: p.profile ?? null,
    fastModel: p.fastModel ?? null,
    smartModel: p.smartModel ?? null,
    voice: p.voice ?? null,
    preparedTurns: p.preparedTurns ?? null,
    modelCalls: p.modelCalls ?? null,
    modelMs: p.modelMs ?? null,
  }));

/**
 * Ce que la brique de téléphonie pousse (création ou mise à jour d'un appel,
 * identifié par `providerId`). Tout est facultatif sauf le numéro : un appel
 * qui débute n'a ni résumé ni fin.
 */
export const ingestCallSchema = z.object({
  direction: callDirectionSchema.default("entrant"),
  status: callStatusSchema.default("en_cours"),
  outcome: callOutcomeSchema.optional(),
  fromNumber: z.string().trim().min(3).max(40),
  toNumber: optionalText,
  callerName: optionalText,
  clientId: z.string().min(1).nullable().optional(),
  appointmentId: z.string().min(1).nullable().optional(),
  startedAt: isoDate.optional(),
  endedAt: isoDate.nullable().optional(),
  durationSec: z.number().int().min(0).optional(),
  intent: optionalText,
  summary: z.string().max(5000).nullable().optional(),
  transcript: z.array(transcriptLineSchema).max(2000).optional(),
  vehicle: optionalText,
  service: optionalText,
  requestedAt: optionalText,
  recordingUrl: z.union([z.string().trim().url(), z.literal(""), z.null()]).optional(),
  /** Demande de rappel : l'appelant a accepté un SMS confirmant sa demande. */
  smsConsent: z.boolean().optional(),
  /** Moteur, révisions et modèles de la brique vocale pour cet appel. */
  provenance: callProvenanceSchema.nullable().optional(),
  /** Demande de rappel ou d'annulation enregistrée pendant l'appel. */
  callback: z
    .object({
      reason: z.string().trim().max(2000).nullable().optional(),
      phone: z.string().trim().max(40).nullable().optional(),
      intent: z.string().trim().max(120).nullable().optional(),
      preferredCallback: z.string().trim().max(200).nullable().optional(),
      appointmentId: z.string().trim().max(80).nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type IngestCallInput = z.infer<typeof ingestCallSchema>;

/** Demande transmise par la brique vocale au moment où Léo l'enregistre (pont, secret partagé). */
export const voiceRequestSchema = z.object({
  callSid: z.string().trim().max(80).nullable().optional(),
  callerNumber: z.string().trim().max(40).nullable().optional(),
  kind: z.enum(["rappel", "annulation"]),
  callerName: optionalText,
  phone: z.string().trim().max(40).nullable().optional(),
  reason: z.string().trim().max(2000).nullable().optional(),
  appointmentId: z.string().trim().max(80).nullable().optional(),
  preferredCallback: z.string().trim().max(200).nullable().optional(),
});
export type VoiceRequestInput = z.infer<typeof voiceRequestSchema>;

export const requestsQuerySchema = z.object({
  status: z.enum(["a_traiter", "traitee"]).optional(),
  clientId: z.string().min(1).optional(),
  appointmentId: z.string().min(1).optional(),
  callId: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});
export type RequestsQuery = z.infer<typeof requestsQuerySchema>;

export const updateRequestSchema = z.object({
  status: z.enum(["a_traiter", "traitee"]),
});
export type UpdateRequestInput = z.infer<typeof updateRequestSchema>;

/** Ce que l'équipe modifie depuis la fiche d'un appel. */
export const updateCallSchema = z.object({
  outcome: callOutcomeSchema.optional(),
  notes: z.string().max(5000).nullable().optional(),
  handled: z.boolean().optional(),
  clientId: z.string().min(1).nullable().optional(),
  appointmentId: z.string().min(1).nullable().optional(),
});
export type UpdateCallInput = z.infer<typeof updateCallSchema>;

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "heure HH:MM attendue");
const dayHoursSchema = z.object({ open: hhmm, close: hhmm }).nullable();

export const DEFAULT_HOURS: Record<Weekday, DayHours | null> = {
  "1": { open: "08:00", close: "17:30" },
  "2": { open: "08:00", close: "17:30" },
  "3": { open: "08:00", close: "17:30" },
  "4": { open: "08:00", close: "17:30" },
  "5": { open: "08:00", close: "17:00" },
  "6": { open: "09:00", close: "13:00" },
  "7": null,
};

export const settingsSchema = z.object({
  enabled: z.boolean().default(true),
  voiceMode: z.enum(VOICE_MODES).default("current"),
  assistantName: z.string().trim().min(1).max(60).default("Léo"),
  voice: z.enum(["feminine", "masculine"]).default("masculine"),
  language: z.enum(["fr", "fr-en"]).default("fr"),
  tone: z.enum(["chaleureux", "professionnel", "direct"]).default("chaleureux"),
  greeting: z
    .string()
    .max(600)
    .default(
      "Bonjour, PROTECTO Saguenay, ici Léo, l'assistant de l'atelier. Comment puis-je vous aider ?",
    ),
  afterHoursMessage: z
    .string()
    .max(600)
    .default(
      "L'atelier est fermé pour le moment. Je peux prendre un rendez-vous ou un message, et l'équipe vous rappellera dès l'ouverture.",
    ),
  instructions: z
    .string()
    .max(5000)
    .default(
      "Toujours confirmer le véhicule (année, marque, modèle) avant de proposer un créneau.\nNe jamais promettre un délai de fin de travaux : c'est le technicien qui le donne à la réception du véhicule.\nPour une garantie antirouille, demander le numéro de garantie ou le NIV.",
    ),
  canBook: z.boolean().default(true),
  canQuotePrices: z.boolean().default(true),
  canTakeMessages: z.boolean().default(true),
  canCheckAppointments: z.boolean().default(true),
  canConfirmPresence: z.boolean().default(true),
  transferNumber: z.string().trim().max(40).default(""),
  priorityDealers: z.boolean().default(true),
  transferPolicy: z.enum(["jamais", "sur_demande", "urgence", "toujours"]).default("sur_demande"),
  hours: z
    .object({
      "1": dayHoursSchema,
      "2": dayHoursSchema,
      "3": dayHoursSchema,
      "4": dayHoursSchema,
      "5": dayHoursSchema,
      "6": dayHoursSchema,
      "7": dayHoursSchema,
    })
    .default(DEFAULT_HOURS),
  notifyPhone: z.string().trim().max(40).default(""),
  notifyEmail: z.union([z.string().trim().email(), z.literal("")]).default(""),
  ownerMobile: z.string().trim().max(40).default(""),
  maxCallMinutes: z.number().int().min(1).max(60).default(8),
});
export const updateSettingsSchema = settingsSchema.partial();
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;

const csv = <T extends z.ZodTypeAny>(item: T) =>
  z
    .string()
    .transform((s) =>
      s
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean),
    )
    .pipe(z.array(item))
    .optional();

export const callsQuerySchema = z.object({
  status: csv(callStatusSchema),
  outcome: csv(callOutcomeSchema),
  q: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional(),
  /** Appels rattachés à une fiche client (section « Appels » de la fiche). */
  clientId: z.string().min(1).max(64).optional(),
});

export const callOriginSchema = z.enum(["telephone", "simulation"]);

/** Paramètres de la liste de supervision : fenêtre de dates, origine, pagination par `before`. */
export const monitoringQuerySchema = callsQuerySchema.extend({
  origin: callOriginSchema.optional(),
  since: isoDate.optional(),
  before: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});
export type MonitoringQuery = z.infer<typeof monitoringQuerySchema>;

/** « 2 min 05 », « 45 s ». */
export function formatCallDuration(sec: number): string {
  if (sec < 60) return `${sec} s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m} min ${String(s).padStart(2, "0")}`;
}
